import { useCallback, useEffect, useState } from "react";
import { Box, Modal, Typography } from "@mui/material";
import { useLocation } from "react-router";
import { HEADER_EST_PDF_DISCOUNT } from "constants/data";
import { PDFViewer } from "@react-pdf/renderer";
import { Close } from "@mui/icons-material";
import ButtonComponent from "components/button/ButtonComponent";
import LayoutMain from "components/Layout/LayoutMain";
import useQuotationDetail from "features/quotation/hooks/useQuotationDetail";
import ImageComponent from "components/image/ImageComponent";
import { Quotation, QuoteDetail } from "features/quotation/types";
import { formatCurrency } from "utils";
import QRCode from "qrcode";
import TableEstDiscount from "features/car_model/components/TableEstDiscount";
import CardCarModelPdf from "features/car_model/components/CardCarModelPdf";
import TableEstResultPdf from "features/car_model/components/TableEstResultPdf";
import PreviewPdfEst from "features/car_model/components/PreviewPdfEst";
import usePrintPdfShortcut from "features/car_model/hook/usePrintPdfShortcut";

const styles = {
  headerInfo: { fontSize: { xs: 16, md: 18, lg: 20 }, fontWeight: "bold" },
  label: { minWidth: 60 },
  value: { flex: 1 },
};
/**
 * EstResult component renders the result page for an estimation result.
 */
export default function EstResultPdf() {
  const { quotation } = useQuotationDetail(true);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [pdfDiscountHeader, setPdfDiscountHeader] = useState(HEADER_EST_PDF_DISCOUNT);

  const [visible, setVisible] = useState<{
    type: "qr" | "sms" | "pdf" | null;
    show: boolean;
  }>({
    type: null,
    show: false,
  });

  const location = useLocation();

  useEffect(() => {
    if (quotation) {
      QRCode.toDataURL(
        `${window.location.origin}/quotation-qr/${quotation.quote_id}?qr_read=yes`,
      ).then((url) => {
        setQrCodeData(url);
      });
    }
  }, [quotation]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("showQr")) {
      setVisible({ type: "qr", show: true });
    }
    if (params.get("showSms")) {
      setVisible({ type: "sms", show: true });
    }
    if (params.get("showPdf") && quotation) {
      setVisible({ type: "pdf", show: true });
    }
  }, [location, quotation]);

  useEffect(() => {
    if (quotation) {
      const discountHeader = [
        {
          label: "交換工賃",
          sx: {
            width: "142px",
            backgroundColor: "var(--green-600)",
          },
        },
        {
          label: "処分",
          sx: {
            width: "142px",
            backgroundColor: quotation.waste_tire_cost ? "var(--green-600)" : "var(--offwhite-500)",
          },
        },
        {
          label: "エアバルブ",
          sx: {
            width: "142px",
            backgroundColor: quotation.rubber_stem_valve_cost
              ? "var(--green-600)"
              : "var(--offwhite-500)",
          },
        },
        {
          label: "窒素",
          sx: {
            width: "142px",
            backgroundColor: quotation.nitrogen_inflation_cost
              ? "var(--green-600)"
              : "var(--offwhite-500)",
          },
        },
        {
          label: "ハブ防錆",
          sx: {
            width: "142px",
            backgroundColor: quotation.hub_rust_prevention_cost
              ? "var(--green-600)"
              : "var(--offwhite-500)",
          },
        },
        {
          label: "パンク補償",
          sx: {
            width: "142px",
            backgroundColor: "var(--offwhite-500)",
          },
        },
      ];
      setPdfDiscountHeader(discountHeader);
    }
  }, [quotation]);

  const prunctureFlagRender = (flag?: Quotation["tire_puncture_flag"]): string => {
    switch (flag) {
      case 2:
        return "2本補償";
      case 4:
        return "4本補償";
      default:
        return "補償なし";
    }
  };

  const renderCostValue = useCallback(
    (cost?: number | null, flag?: number | null) => {
      if (flag === null || flag === 0) return "-";
      if (flag && (cost === 0 || cost === null || cost === undefined)) return "無料";
      return formatCurrency(cost as number);
    },
    [quotation],
  );

  // Handle Ctrl + P to print PDF
  const pdfDocument = useCallback(() => {
    if (!quotation || !qrCodeData) {
      return null;
    }
    return (
      <PreviewPdfEst
        quotationDetail={quotation as QuoteDetail}
        qrCodeData={qrCodeData}
        homeQrImageSrc={
          typeof window !== "undefined" ? `${window.location.origin}/img/home_qr_code.png` : undefined
        }
      />
    );
  }, [quotation, qrCodeData]);

  usePrintPdfShortcut({
    pdfDocument,
    canPrint: () => Boolean(quotation && qrCodeData),
  });

  return (
    <LayoutMain
      sx={{
        flexDirection: "column",
        justifyContent: "space-between",
        mx: "auto",
        maxWidth: "1690px",
        gap: 8,
        paddingX: 0,
        paddingY: 0,
        overflow: "hidden",
        minWidth: "1690px",
      }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            gap: { sm: "50px", xl: "100px" },
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "1690px",
            minWidth: "1690px",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box display="flex">
              <Box sx={{ ...styles.headerInfo, ...styles.label }}>店舗名</Box>
              <Box sx={{ ...styles.headerInfo, ...styles.value }}>：{quotation?.store_name}</Box>
            </Box>
            <Box display="flex">
              <Box sx={{ ...styles.headerInfo, ...styles.label }}>住所</Box>
              <Box sx={{ ...styles.headerInfo, ...styles.value }}>：{quotation?.address}</Box>
            </Box>
            <Box display="flex">
              <Box sx={{ ...styles.headerInfo, ...styles.label }}>電話</Box>
              <Box sx={{ ...styles.headerInfo, ...styles.value }}>
                ：{quotation?.tel_no} &nbsp;&nbsp;&nbsp; 見積担当：{quotation?.quote_by}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ImageComponent
                sx={{ width: "80px", border: "unset" }}
                src="/img/home_qr_code.png"
                alt="home_qr_code"
                objectFit="contain"
              />
              <Typography sx={{ fontSize: { xs: 12, md: 13, lg: 14 }, whiteSpace: "nowrap" }}>
                当社グループは個人情報保護法に準拠し、適切な個人情報の取り扱いをしております。
                <br />
                プライバシーポリシーは当社HPに記載及び店頭に掲示しております。
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, fontWeight: "bold" }}>
              金額は全て税込となります
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "flex-start",
            maxWidth: "1690px",
            minWidth: "1690px",
            borderBottom: "1px solid var(--gray-250)",
            pb: 2,
          }}
        >
          {quotation && <CardCarModelPdf quotation={quotation} showDates />}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            width: "100%",
            gap: "6px",
            boxSizing: "border-box",
            maxWidth: "1690px",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Typography sx={{ display: "flex", justifyContent: "end", fontSize: { xs: 16, md: 18, lg: 20 } }}>※価格は1本あたり
            </Typography>
            <TableEstDiscount
              disableRounding
              headerData={pdfDiscountHeader}
              rowData={[
                {
                  label: quotation?.replac_labor_cost
                    ? formatCurrency(quotation.replac_labor_cost)
                    : "0",
                  sx: { borderBottom: "none", px: 1 },
                },
                {
                  label: renderCostValue(
                    quotation?.waste_tire_cost,
                    quotation?.waste_tire_cost_flag,
                  ),
                  sx: { borderBottom: "none", px: 1 },
                },
                {
                  label: renderCostValue(
                    quotation?.rubber_stem_valve_cost,
                    quotation?.rubber_stem_valve_flag,
                  ),
                  sx: { borderBottom: "none", px: 1 },
                },
                {
                  label: renderCostValue(
                    quotation?.nitrogen_inflation_cost,
                    quotation?.nitrogen_inflation_flag,
                  ),
                  sx: { borderBottom: "none", px: 1 },
                },
                {
                  label: renderCostValue(
                    quotation?.hub_rust_prevention_cost,
                    quotation?.hub_rust_prevention_flag,
                  ),
                  sx: { borderBottom: "none", px: 1 },
                },
                {
                  label: prunctureFlagRender(quotation?.tire_puncture_flag),
                  sx: { borderBottom: "none" },
                },
              ]}
            />
          </Box>
          {quotation && quotation.tbl_quote_detail_tire && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              {quotation.tbl_quote_detail_tire.map((item) => (
                <Box
                  key={item.tire_item_cd}
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <TableEstResultPdf quotation={item} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        open={visible.show && visible.type === "pdf"}
        onClose={() => setVisible((prev) => ({ ...prev, show: false }))}
        style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <>
          <ButtonComponent
            sx={{
              display: "flex",
              width: "40px",
              borderRadius: "10px",
              position: "absolute",
              bottom: "24px",
              top: 0,
              right: 0,
              backgroundColor: "var(--white)",
              "&:hover": {
                backgroundColor: "var(--silver-300)",
              },
              zIndex: 1,
            }}
            onClick={() => setVisible((prev) => ({ ...prev, show: false }))}
          >
            <Close
              sx={{
                width: "40px",
              }}
            />
          </ButtonComponent>
          <Box
            sx={{
              width: { sm: "600px", lg: "60%" },
              height: "100%",
              position: "relative",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {quotation && qrCodeData && (
              <PDFViewer
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                  alignItems: "center",
                  flexDirection: "row",
                }}
              >
                <PreviewPdfEst
                  quotationDetail={quotation}
                  qrCodeData={qrCodeData}
                  homeQrImageSrc={
                    typeof window !== "undefined"
                      ? `${window.location.origin}/img/home_qr_code.png`
                      : undefined
                  }
                />
              </PDFViewer>
            )}
          </Box>
        </>
      </Modal>

      <Modal
        open={visible.show && visible.type !== "pdf"}
        onClose={() => setVisible((prev) => ({ ...prev, show: false }))}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "var(--white)",
            width: "550px",
            height: "545px",
            alignSelf: "center",
            flexDirection: "column",
            padding: "55px",
            borderRadius: "32px",
            boxSizing: "border-box",
          }}
        >
          {visible.type === "qr" ? (
            <ImageComponent sx={{ width: "90%" }} src="/img/icons/qr-big.svg" alt="QRコード" />
          ) : (
            <Box>
              <Typography sx={{ fontSize: "56px", fontWeight: "medium", color: "var(--gray-700)" }}>
                SMS送信
              </Typography>
              <Box sx={{ mt: "89px", pl: 2, mb: "69px" }}>
                <Typography
                  sx={{ fontSize: "33px", fontWeight: "medium", color: "var(--gray-700)" }}
                >
                  見積もり書を
                </Typography>
                <Typography
                  sx={{ fontSize: "33px", fontWeight: "medium", color: "var(--gray-700)" }}
                >
                  090-0000-0000へ
                </Typography>
                <Typography
                  sx={{ fontSize: "33px", fontWeight: "medium", color: "var(--gray-700)" }}
                >
                  SMS送信しました
                </Typography>
              </Box>
            </Box>
          )}
          <ButtonComponent
            sx={{ width: "116px", borderRadius: "10px", marginTop: 1 }}
            label="閉じる"
            onClick={() => setVisible((prev) => ({ ...prev, show: false }))}
          />
        </Box>
      </Modal>
    </LayoutMain>
  );
}
